import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { attributeLabel } from "../../utils/viewConfig";
import { Hint, Surface, menuPaper, rowSx } from "./WorkspaceUi";
import { allAttributes, attributeFacts, commonAttributes, filledText, shortExample, startingAttributes } from "./workspaceFormat";

// The shown details first, in the order a reader meets them, then everything
// else the objects of this type carry. "name" is first and never moves.
export const attributeRows = (attributes = [], facts = []) => {
  const shown = attributes.filter((name) => name !== "name");
  const byName = new Map(facts.map((fact) => [fact.name, fact]));
  const rest = facts.filter((fact) => fact.name !== "name" && !shown.includes(fact.name));
  return [
    { name: "name", shown: true, fixed: true, ...(byName.get("name") || { filled: null, objects: null, example: "" }) },
    ...shown.map((name) => ({ name, shown: true, fixed: false, missing: !byName.has(name), ...(byName.get(name) || { filled: null, objects: null, example: "" }) })),
    ...rest.map((fact) => ({ ...fact, shown: false, fixed: false })),
  ];
};

// What the reader sees for one object type: which details, and in which
// order. Every count and example comes from the chosen database's catalogue.
export default function ViewAttributes({ entry, object, label, disabled, otherTypes = [], onChange, onToggleType, onCopyFrom, onRefresh }) {
  const [menu, setMenu] = useState(null); // { anchor, name }
  const [copying, setCopying] = useState(null);
  const [dragging, setDragging] = useState(null);

  if (!label) {
    return (
      <Typography variant="body2" color="text.secondary">
        Choose an object type on the left to decide which of its details people see.
      </Typography>
    );
  }

  const facts = attributeFacts(entry);
  const attributes = object?.attributes || [];
  const rows = attributeRows(attributes, facts);
  const shownNames = attributes.filter((name) => name !== "name");
  const common = commonAttributes(facts);
  // countsKnown is false only while this session hasn't read this type yet
  // (a big library, read in stages) - never "this type carries nothing".
  const unread = Boolean(entry) && entry.countsKnown === false;

  const set = (next) => onChange(["name", ...next.filter((name) => name !== "name")]);
  const toggle = (name, on) => set(on ? [...shownNames, name] : shownNames.filter((other) => other !== name));
  const move = (name, to) => {
    const from = shownNames.indexOf(name);
    if (from < 0 || to < 0 || to >= shownNames.length || from === to) return;
    const next = [...shownNames];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    set(next);
  };

  if (!object) {
    return (
      <Box>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This object type isn't shown, so its details aren't either.
        </Typography>
        {!disabled && (
          <Button size="small" variant="outlined" onClick={() => onToggleType(true)}>
            Show this object type
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        {unread
          ? `${shownNames.length + 1} ${shownNames.length + 1 === 1 ? "detail" : "details"} shown, in this order. The name is always first.`
          : `${shownNames.length + 1} of ${Math.max(facts.length, shownNames.length + 1)} details shown, in this order. The name is always first.`}
      </Typography>

      {unread && (
        <Hint
          sx={{ mb: 1.25 }}
          title="This object type's details haven't been read yet"
          action={
            onRefresh ? (
              <Button size="small" onClick={onRefresh} aria-label={`Refresh ${label}'s details`}>
                Refresh
              </Button>
            ) : null
          }
        >
          Its name still shows. Read this database's object types again to see what else it carries.
        </Hint>
      )}

      {!disabled && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {!unread && (
            <Button size="small" variant="outlined" onClick={() => set(startingAttributes(facts.map((f) => f.name)).filter((n) => n !== "name"))}>
              Name and description only
            </Button>
          )}
          {!unread && (
            <Button size="small" variant="outlined" onClick={() => set(allAttributes(facts.map((f) => f.name)).filter((n) => n !== "name"))}>
              All details
            </Button>
          )}
          {common.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              title="Details half the objects or more actually hold"
              onClick={() => set(common.filter((n) => n !== "name"))}
            >
              Only details most objects have
            </Button>
          )}
          {otherTypes.length > 0 && (
            <Button size="small" variant="outlined" onClick={(e) => setCopying(e.currentTarget)}>
              Copy from…
            </Button>
          )}
        </Stack>
      )}

      <Surface>
        {rows.map((row, index) => {
          const position = shownNames.indexOf(row.name);
          const fill = filledText(row);
          const example = shortExample(row.example);
          return (
            <Box
              key={row.name}
              sx={{ ...rowSx(!index), py: 1, opacity: row.missing ? 0.7 : 1, bgcolor: row.shown ? "transparent" : brand.shell }}
              draggable={!disabled && row.shown && !row.fixed}
              onDragStart={() => setDragging(row.name)}
              onDragOver={(e) => dragging && row.shown && !row.fixed && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging && row.shown && !row.fixed) move(dragging, position);
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {row.shown && !row.fixed && !disabled ? (
                  <DragIndicatorRoundedIcon fontSize="small" sx={{ color: brand.muted, cursor: "grab" }} aria-hidden />
                ) : (
                  <Box sx={{ width: 20 }} />
                )}
                <Switch
                  size="small"
                  checked={row.shown}
                  disabled={disabled || row.fixed}
                  onChange={(e) => toggle(row.name, e.target.checked)}
                  inputProps={{ "aria-label": `Show ${row.name} for ${label}` }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", overflowWrap: "anywhere" }}>{attributeLabel(row.name)}</Typography>
                    {row.fixed && <Chip size="small" label="Always shown" sx={{ height: 18, fontSize: "0.68rem", bgcolor: brand.canvas, border: `1px solid ${brand.line}` }} />}
                    {row.missing && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Not used in this database"
                        sx={{ height: 18, fontSize: "0.68rem", color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
                      />
                    )}
                  </Stack>
                  {(fill || example) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                      {[fill, example && `e.g. “${example}”`].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Box>
                {row.shown && !row.fixed && !disabled && (
                  <Button size="small" aria-label={`Order of ${row.name} in ${label}`} onClick={(e) => setMenu({ anchor: e.currentTarget, name: row.name, position })}>
                    {position + 2}
                  </Button>
                )}
              </Stack>
            </Box>
          );
        })}
      </Surface>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} {...menuPaper}>
        <MenuItem
          disabled={!menu || menu.position <= 0}
          onClick={() => {
            move(menu.name, menu.position - 1);
            setMenu(null);
          }}
        >
          Move earlier
        </MenuItem>
        <MenuItem
          disabled={!menu || menu.position >= shownNames.length - 1}
          onClick={() => {
            move(menu.name, menu.position + 1);
            setMenu(null);
          }}
        >
          Move later
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggle(menu.name, false);
            setMenu(null);
          }}
        >
          Don't show it
        </MenuItem>
      </Menu>

      <Menu anchorEl={copying} open={Boolean(copying)} onClose={() => setCopying(null)} {...menuPaper}>
        {otherTypes.map((type) => (
          <MenuItem
            key={type.type}
            onClick={() => {
              onCopyFrom(type.type);
              setCopying(null);
            }}
          >
            {type.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
